/**
 * Dev tools tab — staging only.
 *
 * Create an order for this patient with REAL routing, and walk it up the
 * lifecycle by driving the real pharmacy webhook handlers. Replaces
 * hand-seeding rows, which was slow and quietly misleading: a hand-written
 * `metadata.create_order` snapshot can make a reship pass on staging and still
 * fail in prod.
 *
 * The tab is mounted by ClinicalCustomerScreen only when the backend serves
 * `/admin/dev-tools/capabilities` — in production those routes do not exist,
 * so it can never appear there.
 *
 * Lives in its own file rather than CustomerTabs.tsx, which is already ~2k
 * lines.
 */
import { useMemo, useState } from 'react';

import {
  useAdvanceOrder,
  useCreateTestOrder,
  useDevToolsCapabilities,
  useDevToolsProducts,
} from '@/lib/api/devToolsHooks';
import { useClinicalPatientOrders } from '@/lib/api/clinicalAdminHooks';
import type { AdvanceTarget, DevToolsProduct } from '@/types/devTools';
import type { PharmacyOrder } from '@/types/clinicalAdmin';

import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Td,
  Th,
  fmtDateTime,
} from './ui';

const ADVANCE_TARGETS: AdvanceTarget[] = [
  'processing',
  'shipped',
  'delivered',
  'cancelled',
];

// Terminal states have nothing left to advance to — every handler's
// forward-transition guard would refuse the write anyway, so offering the
// buttons would just manufacture confusing no-ops.
const TERMINAL = new Set(['delivered', 'cancelled', 'failed', 'superseded']);

function statusTone(status: string | null | undefined) {
  switch (status) {
    case 'delivered':
      return 'good' as const;
    case 'shipped':
    case 'processing':
      return 'accent' as const;
    case 'cancelled':
    case 'failed':
      return 'danger' as const;
    default:
      return 'neutral' as const;
  }
}

export function DevToolsTab({ patientId }: { patientId: string }) {
  const { data: caps } = useDevToolsCapabilities();
  const { data: productsData, isLoading: productsLoading } =
    useDevToolsProducts();
  const { data: ordersData, isLoading: ordersLoading } =
    useClinicalPatientOrders(patientId);

  const [productId, setProductId] = useState('');
  const [writeRx, setWriteRx] = useState(true);
  const [log, setLog] = useState<string[]>([]);

  const createOrder = useCreateTestOrder(patientId);
  const advance = useAdvanceOrder(patientId);

  const products = useMemo(
    () => productsData?.products ?? [],
    [productsData],
  );
  const selected: DevToolsProduct | undefined = useMemo(
    () => products.find((p) => p.product_id === productId),
    [products, productId],
  );

  const say = (line: string) =>
    setLog((prev) => [`${new Date().toLocaleTimeString()}  ${line}`, ...prev]);

  const onCreate = async () => {
    if (!productId) return;
    try {
      const res = await createOrder.mutateAsync({
        product_id: productId,
        write_prescription: writeRx,
      });
      if (res.prescription?.warning) say(`⚠ ${res.prescription.warning}`);
      if (res.order_id) {
        say(
          `created ${res.order_number ?? res.order_id} — routed to ` +
            `${res.routed_to ?? 'unknown'}, status ${res.status ?? '?'}`,
        );
      } else {
        say(`no order created${res.detail ? ` — ${res.detail}` : ''}`);
      }
    } catch (e) {
      say(`create failed — ${(e as Error).message}`);
    }
  };

  const onAdvance = async (order: PharmacyOrder, to: AdvanceTarget) => {
    try {
      const res = await advance.mutateAsync({ orderId: order.id, to });
      say(
        `${res.order_number ?? res.order_id}: ${res.status_before} → ` +
          `${res.status_after ?? '?'}`,
      );
      // Render every step, including the ones that did nothing. A skipped
      // rung reported as a completed one is the exact false confidence this
      // tool is meant to remove.
      for (const s of res.steps) {
        if (s.note) say(`   ${s.to}: ${s.note}`);
        else if (!s.changed) say(`   ${s.to}: no change (via ${s.via})`);
      }
    } catch (e) {
      say(`advance failed — ${(e as Error).message}`);
    }
  };

  const orders = ordersData?.orders ?? [];
  const busy = createOrder.isPending || advance.isPending;

  return (
    <div className="flex flex-col gap-16">
      {/* What this environment is actually wired to. Worth showing rather
          than assuming: "staging" reads as "nothing escapes", and some of
          these are production credentials. */}
      {caps && (
        <Card title={`Environment — ${caps.app_env}`}>
          <div className="px-15 py-12 flex flex-col gap-8">
            <div className="grid grid-cols-2 gap-x-16 gap-y-4 type-label-sm text-ink-muted">
              <span>Supabase</span>
              <span className="text-ink">{caps.supabase_project_ref}</span>
              <span>OptioRx</span>
              <span className="text-ink">{caps.optiorx_api_url}</span>
              <span>Healthie tasks → user</span>
              <span className="text-ink">{caps.healthie_task_assignee_id}</span>
              <span>eRx mode</span>
              <span className="text-ink">
                {caps.erx_mode}
                {caps.ppi_pdf_enabled ? ' · PPI PDF on' : ''}
              </span>
            </div>
            {caps.live_side_effects.length > 0 && (
              <div className="flex flex-col gap-4 pt-8 border-t border-line">
                {caps.live_side_effects.map((w) => (
                  <div key={w} className="type-label-sm text-danger">
                    ⚠ {w}
                  </div>
                ))}
                <div className="type-label-sm text-ink-muted">
                  Use a patient whose chart, inbox and phone you own.
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card title="Create a test order">
        <div className="px-15 py-12 flex flex-col gap-10">
          <p className="type-label-sm text-ink-muted">
            Routing is the real thing — state licensing, cohort gate, stock and
            priority rules all apply, so the pharmacy this lands on is the one a
            real checkout would have picked.
          </p>

          {productsLoading ? (
            <LoadingState label="Loading products…" />
          ) : (
            <div className="flex flex-wrap items-center gap-10">
              <select
                className="border border-line rounded bg-page px-10 py-8 type-label-sm min-w-[320px]"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              >
                <option value="">Select a product…</option>
                {products.map((p) => (
                  <option key={p.product_id} value={p.product_id}>
                    {p.medication_name ?? p.product_id}
                    {p.prescribable ? '' : '  ⚠ not prescribable'}
                  </option>
                ))}
              </select>

              <label className="flex items-center gap-6 type-label-sm text-ink-muted">
                <input
                  type="checkbox"
                  checked={writeRx}
                  onChange={(e) => setWriteRx(e.target.checked)}
                />
                Write the medication record first
              </label>

              <button
                type="button"
                className="rounded bg-ink text-page px-14 py-8 type-label-sm disabled:opacity-50"
                disabled={!productId || busy}
                onClick={onCreate}
              >
                {createOrder.isPending ? 'Creating…' : 'Create order'}
              </button>
            </div>
          )}

          {/* The catalog mismatch that otherwise presents as a routing bug:
              the name is written from pharmacy_products.medication_name but
              matched against prescription_product_map. */}
          {selected && !selected.prescribable && (
            <div className="type-label-sm text-danger">
              ⚠ {selected.prescribable_note}
            </div>
          )}
          {selected && !writeRx && (
            <div className="type-label-sm text-ink-muted">
              Without a medication record this will park in{' '}
              <code>awaiting_prescription</code> — valid to test, just usually
              not what you wanted.
            </div>
          )}
        </div>
      </Card>

      <Card title="Orders">
        {ordersLoading ? (
          <LoadingState label="Loading orders…" />
        ) : orders.length === 0 ? (
          <EmptyState>No orders for this patient yet.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line">
                  <Th>Order</Th>
                  <Th>Pharmacy</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                  <Th>Advance to</Th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const terminal = TERMINAL.has(String(o.status ?? ''));
                  return (
                    <tr key={o.id} className="border-b border-line last:border-0">
                      <Td>
                        <span className="type-label-sm">
                          {o.order_number ?? o.id.slice(0, 8)}
                        </span>
                        {!o.partner_order_id && (
                          <div className="type-10 text-ink-muted">
                            not transmitted
                          </div>
                        )}
                      </Td>
                      <Td className="type-label-sm">{o.partner_key ?? '—'}</Td>
                      <Td>
                        <Badge tone={statusTone(o.status)}>
                          {o.status ?? '—'}
                        </Badge>
                      </Td>
                      <Td className="type-label-sm text-ink-muted">
                        {fmtDateTime(o.created_at)}
                      </Td>
                      <Td>
                        <div className="flex flex-wrap gap-6">
                          {ADVANCE_TARGETS.map((t) => (
                            <button
                              key={t}
                              type="button"
                              className="border border-line rounded px-8 py-4 type-10 uppercase tracking-wide disabled:opacity-40"
                              disabled={busy || terminal}
                              onClick={() => onAdvance(o, t)}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {log.length > 0 && (
        <Card
          title="Activity"
          action={
            <button
              type="button"
              className="type-label-sm text-ink-muted"
              onClick={() => setLog([])}
            >
              Clear
            </button>
          }
        >
          <pre className="px-15 py-12 type-label-sm text-ink-muted whitespace-pre-wrap">
            {log.join('\n')}
          </pre>
        </Card>
      )}
    </div>
  );
}

export function DevToolsUnavailable() {
  return <ErrorState>Dev tools are not enabled in this environment.</ErrorState>;
}
